import { useCallback, useMemo } from "react";
import { useParams } from "react-router-dom";

import { queryClient } from "App";
import BaseTableV3 from "components/BaseTableV3/BaseTableV3";
import { getNextQueryParams } from "components/helpers";
import RuvixxDate from "components/RuvixxDate";
import useMetaSyncInProgress, {
  META_SYNC_POLL_INTERVAL_MS,
} from "hooks/useMetaSyncInProgress";
import MetaSandboxCampaignService from "services/MetaSandboxCampaign";

const sandboxCampaignsQueryKey = accountId => [
  "meta-sandbox-campaigns",
  accountId,
];

const CAMPAIGN_COLUMNS = [
  {
    header: "ID",
    id: "ID",
    accessorKey: "id",
    size: 50,
    cell: ({ row }) => (
      <p style={{ fontWeight: "bold" }}>{row.getValue("ID")}</p>
    ),
  },
  { id: "Name", accessorKey: "name", header: "Name", size: 220 },
  { id: "Objective", accessorKey: "objective", header: "Objective", size: 170 },
  { id: "Status", accessorKey: "status", header: "Status", size: 120 },
  {
    id: "Delivery",
    accessorKey: "effective_status",
    header: "Delivery",
    size: 140,
  },
  {
    id: "Daily Budget",
    accessorKey: "daily_budget",
    header: "Daily Budget",
    size: 130,
  },
  {
    id: "Start",
    accessorKey: "start_time",
    header: "Start",
    size: 160,
    cell: ({ row }) => {
      const date = row.getValue("Start");
      return date ? <RuvixxDate date={date} /> : null;
    },
  },
];

const MetaSandboxCampaignsTab = () => {
  const { accountId } = useParams();

  const columns = useMemo(() => CAMPAIGN_COLUMNS, []);
  const isSyncing = useMetaSyncInProgress(accountId, "sandbox");

  const invalidateCampaigns = useCallback(
    () =>
      queryClient.invalidateQueries({
        queryKey: sandboxCampaignsQueryKey(accountId),
      }),
    [accountId]
  );

  const fetchCampaigns = useCallback(
    async queryParams => {
      const next = getNextQueryParams(queryParams, columns);
      return MetaSandboxCampaignService.list(accountId, next);
    },
    [accountId, columns]
  );

  return (
    <div className="tw:flex tw:flex-col tw:flex-1 tw:min-h-0 tw:min-w-0">
      <BaseTableV3
        columns={columns}
        header="Sandbox Campaigns"
        tableName="meta_sandbox_campaigns"
        queryKey={sandboxCampaignsQueryKey(accountId)}
        fetchData={fetchCampaigns}
        invalidateData={invalidateCampaigns}
        refetchInterval={isSyncing ? META_SYNC_POLL_INTERVAL_MS : undefined}
        enableSearch
        enableSettings
      />
    </div>
  );
};

export default MetaSandboxCampaignsTab;
